import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getDashboardStats, getExpenses, getReportsSummary, getUsers } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import {
  Receipt,
  Clock,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Plus,
  ArrowRight,
  Wallet,
  FileText,
  Users
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const COLORS = ["hsl(222, 47%, 11%)", "hsl(142, 71%, 45%)", "hsl(210, 40%, 60%)", "hsl(0, 84.2%, 60.2%)", "hsl(38, 92%, 50%)"];

const statusLabels = {
  draft: "Brouillon",
  submitted: "En attente",
  approved: "Approuvé",
  rejected: "Rejeté",
  reimbursed: "Remboursé"
};

const StatCard = ({ title, value, description, icon: Icon, trend, loading }) => (
  <Card className="stat-card" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      <Icon className="h-5 w-5 text-muted-foreground" />
    </CardHeader>
    <CardContent>
      {loading ? (
        <Skeleton className="h-8 w-24" />
      ) : (
        <>
          <div className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {value}
          </div>
          {description && (
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          )}
        </>
      )}
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { user, hasRole } = useAuth();
  const isManager = hasRole(["admin", "manager", "accountant"]);

  const [stats, setStats] = useState(null);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, [userFilter]);

  useEffect(() => {
    if (isManager) {
      getUsers().then(res => setUsers(res.data)).catch(() => {});
    }
  }, [isManager]);

  const fetchData = async () => {
    try {
      const params = userFilter !== "all" ? { user_id: userFilter } : {};
      const [statsRes, expensesRes, summaryRes] = await Promise.all([
        getDashboardStats(params),
        getExpenses({ ...params, limit: 5 }),
        getReportsSummary(params)
      ]);
      
      setStats(statsRes.data);
      setRecentExpenses(expensesRes.data.slice(0, 5));
      
      // Format monthly data for chart
      const monthlyDataFormatted = Object.entries(summaryRes.data.by_month || {}).map(([month, amount]) => ({
        name: month,
        montant: amount
      }));
      setMonthlyData(monthlyDataFormatted.slice(-6));
      
      // Format category data for pie chart
      const categoryDataFormatted = Object.entries(summaryRes.data.by_category || {})
        .map(([cat, amount]) => ({ name: cat, value: amount }))
        .sort((a, b) => b.value - a.value);
      setCategoryData(categoryDataFormatted);
      
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getStatusClass = (status) => {
    return `status-badge status-${status}`;
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Bonjour, {user?.first_name}</h1>
          <p className="page-description">Voici un aperçu de vos notes de frais</p>
        </div>
        <div className="flex items-center gap-3">
          {isManager && users.length > 0 && (
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-[200px]" data-testid="user-filter">
                <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Collaborateur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les collaborateurs</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Link to="/expenses/new">
            <Button className="gap-2" data-testid="new-expense-btn">
              <Plus className="h-4 w-4" />
              Nouvelle dépense
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Ce mois"
          value={formatCurrency(stats?.month_amount)}
          description={`${stats?.month_count || 0} dépenses`}
          icon={Wallet}
          loading={loading}
        />
        <StatCard
          title="En attente"
          value={stats?.pending_approval || 0}
          description="À valider"
          icon={Clock}
          loading={loading}
        />
        <StatCard
          title="Approuvées"
          value={stats?.pending_reimbursement || 0}
          description="À rembourser"
          icon={CheckCircle2}
          loading={loading}
        />
        <StatCard
          title={`Total ${new Date().getFullYear()}`}
          value={formatCurrency(stats?.total_amount)}
          description={`${stats?.total_expenses || 0} dépenses`}
          icon={Receipt}
          loading={loading}
        />
      </div>

      {/* Charts */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-12">
        {/* Monthly Chart */}
        <Card className="lg:col-span-8" data-testid="monthly-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Évolution mensuelle
            </CardTitle>
            <CardDescription>Dépenses des 6 derniers mois</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214.3, 31.8%, 91.4%)" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="hsl(215.4, 16.3%, 46.9%)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(215.4, 16.3%, 46.9%)" />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      backgroundColor: 'hsl(0, 0%, 100%)', 
                      border: '1px solid hsl(214.3, 31.8%, 91.4%)',
                      borderRadius: '6px'
                    }}
                  />
                  <Bar dataKey="montant" fill="hsl(222, 47%, 11%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Pie Chart */}
        <Card className="lg:col-span-4" data-testid="category-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Répartition par type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[250px] w-full" />
            ) : categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: 'hsl(0, 0%, 100%)',
                      border: '1px solid hsl(214.3, 31.8%, 91.4%)',
                      borderRadius: '6px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Aucune donnée
              </div>
            )}
            {!loading && categoryData.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-4 justify-center">
                {categoryData.map((item, index) => (
                  <div key={item.name} className="flex items-center gap-2 text-xs">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-muted-foreground">{item.name}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Expenses */}
      <Card data-testid="recent-expenses">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dépenses récentes
            </CardTitle>
            <CardDescription>Vos dernières notes de frais</CardDescription>
          </div>
          <Link to="/expenses">
            <Button variant="ghost" className="gap-2" data-testid="view-all-expenses">
              Voir tout
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : recentExpenses.length > 0 ? (
            <div className="space-y-4">
              {recentExpenses.map((expense) => (
                <Link
                  key={expense.id}
                  to={`/expenses/${expense.id}`}
                  className="flex items-center justify-between p-4 rounded-md border hover:border-primary/20 hover:bg-muted/50 transition-colors"
                  data-testid={`expense-item-${expense.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">{expense.description}</p>
                      <p className="text-sm text-muted-foreground">
                        {expense.category_name} • {expense.date}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={getStatusClass(expense.status)}>
                      {statusLabels[expense.status]}
                    </span>
                    <span className="font-semibold tabular-nums">
                      {formatCurrency(expense.amount)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <img
                src="https://images.unsplash.com/photo-1717994818194-5760d533cab6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwM2QlMjBzaGFwZXMlMjBhYnN0cmFjdCUyMGZpbmFuY2V8ZW58MHx8fHwxNzcxOTI2Mzk1fDA&ixlib=rb-4.1.0&q=85"
                alt="No expenses"
                className="w-32 h-32 mx-auto mb-4 rounded-lg opacity-50"
              />
              <p className="text-muted-foreground mb-4">Aucune dépense pour le moment</p>
              <Link to="/expenses/new">
                <Button data-testid="empty-new-expense-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Créer votre première dépense
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
